import { expectEOF, expectSingleResult } from 'typescript-parsec';

import type { AST } from './ast';
import { lexer } from './lexer';
import { EXPRESSION } from './parser';
export type { AST } from './ast';

/**
 * Parses a string into an AST.
 */
export function parse(expr: string): AST {
    const result = expectEOF(EXPRESSION.parse(lexer.parse(expr)));
    return expectSingleResult(result);
}

/**
 * Pretty-prints an AST.
 */
export function print(ast: AST): string {
    return _print(ast, 0);

    function _print(ast: AST, level: number): string {
        const space = '  ';
        const indent = new Array(level).fill(space).join('');
        switch (ast.type) {
            case 'status':
                return ast.status;
            case 'unary': {
                switch (ast.operator) {
                    case '-':
                        return `-${_print(ast.body, level)}`;
                    default:
                        ast satisfies never;
                        throw Error(`Unknown operator in ${JSON.stringify(ast)}`);
                }
            }
            case 'binary':
                return `${_print(ast.left, level)} ${ast.operator} ${_print(ast.right, level)}`;
            case 'combination':
                return `${_print(ast.left, level)} ${ast.operator} ${_print(ast.right, level)}`;
            case 'not':
                return `not ${_print(ast.body, level)}`;
            case 'comparison':
                return `${_print(ast.left, level)} ${ast.operator} ${_print(ast.right, level)}`;
            case 'conditional':
                return `if ${_print(ast.condition, level)} then\n${
                    indent + space
                }${_print(ast.then, level + 1)}\n${indent}else\n${indent + space}${_print(ast.else, level + 1)}`;
            case 'parens':
                return `(${_print(ast.body, level)})`;
            case 'number':
                return `${ast.value}`;
            case 'identifier':
                return ast.name;
            default:
                ast satisfies never;
                throw Error(`Unknown type in ${JSON.stringify(ast)}`);
        }
    }
}

/**
 * The expected status of an ad. This is the output of the evaluator.
 */
export enum Status {
    Active = 'active',
    Paused = 'paused',
    Skip = 'skip',
}

/**
 * The values that can be referenced in an expression. This is the input of the evaluator.
 */
export type Values = {
    cost: number;
    clicks: number;
    installs: number;
    cpx: number;
};

/**
 * The error thrown when a division by zero occurs.
 */
export class DivisionByZeroError extends Error {}

/**
 * Evaluates an AST into a status using the given values.
 */
export function evaluate(ast: AST, values: Values): Status {
    return _evaluate(ast);

    function _evaluate(ast: AST): any {
        switch (ast.type) {
            case 'status':
                switch (ast.status) {
                    case 'active':
                        return Status.Active;
                    case 'paused':
                        return Status.Paused;
                    case 'skip':
                        return Status.Skip;
                    default: {
                        ast.status satisfies never;
                        throw Error(`Invalid status in ${JSON.stringify(ast)}`);
                    }
                }
            case 'unary':
                switch (ast.operator) {
                    case '-':
                        return -_evaluate(ast.body);
                    default: {
                        ast satisfies never;
                        throw Error(`Invalid unary operator in ${JSON.stringify(ast)}`);
                    }
                }
            case 'binary':
                switch (ast.operator) {
                    case '+':
                        return _evaluate(ast.left) + _evaluate(ast.right);
                    case '-':
                        return _evaluate(ast.left) - _evaluate(ast.right);
                    case '*':
                        return _evaluate(ast.left) * _evaluate(ast.right);
                    case '/': {
                        const denominator = _evaluate(ast.right);
                        if (denominator === 0) {
                            throw new DivisionByZeroError(
                                `${print(ast.right)} is zero in the expression "${print(ast)}"`,
                            );
                        }

                        return _evaluate(ast.left) / denominator;
                    }
                    default: {
                        ast satisfies never;
                        throw Error(`Invalid binary operator in ${JSON.stringify(ast)}`);
                    }
                }
            case 'combination':
                switch (ast.operator) {
                    case 'and':
                        return _evaluate(ast.left) && _evaluate(ast.right);
                    case 'or':
                        return _evaluate(ast.left) || _evaluate(ast.right);
                    default: {
                        ast satisfies never;
                        throw Error(`Invalid combination operator in ${JSON.stringify(ast)}`);
                    }
                }
            case 'not':
                return !_evaluate(ast.body);
            case 'comparison':
                switch (ast.operator) {
                    case '<':
                        return _evaluate(ast.left) < _evaluate(ast.right);
                    case '>':
                        return _evaluate(ast.left) > _evaluate(ast.right);
                    case '<=':
                        return _evaluate(ast.left) <= _evaluate(ast.right);
                    case '>=':
                        return _evaluate(ast.left) >= _evaluate(ast.right);
                    default: {
                        ast satisfies never;
                        throw Error(`Invalid comparison operator in ${JSON.stringify(ast)}`);
                    }
                }
            case 'conditional':
                return _evaluate(ast.condition) ? _evaluate(ast.then) : _evaluate(ast.else);
            case 'parens':
                return _evaluate(ast.body);
            case 'number':
                return ast.value;
            case 'identifier':
                return values[ast.name];
            default: {
                ast satisfies never;
                throw Error(`Invalid type in ${JSON.stringify(ast)}`);
            }
        }
    }
}
